// src/components/TuneRecorder.js
import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import dexie from '../offlineDB';
import { syncTuneRecordings } from '../utils/syncTuneRecordings';
import { useRecording } from '../RecordingContext';
import { useLocation } from 'react-router-dom';

const TuneRecorder = ({ tune, user, recordings = [], onUpdateRecordings, onRecordingSaved }) => {
  const [label, setLabel] = useState('');
  const audioRef = useRef(null);

  const {
    isRecording,
    startRecording,
    stopRecording,
    originPath,
    setOriginPath,
    elapsedTime,
  } = useRecording();

  const location = useLocation();

  useEffect(() => {
    // When mounted, set originPath if not set
    if (isRecording && !originPath) {
      setOriginPath(location.pathname);
    }
  }, [isRecording, originPath, location.pathname, setOriginPath]);

  const handleStartRecording = async () => {
    await startRecording(location.pathname);
  };

  const handleStopRecording = async () => {
    try {
      const result = await stopRecording();
      if (!result) return;

      const { blob, url } = result;

      console.log('[TuneRecorder] BLOB SIZE:', blob?.size);

      if (!blob || blob.size === 0) {
        alert("Recording failed — no audio was captured.");
        return;
      }

      const id = uuidv4();
      const createdAt = new Date().toISOString();

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onloadedmetadata = async () => {
        let duration = Math.round(audio.duration);
        if (!isFinite(duration) || isNaN(duration)) {
          console.warn('[TuneRecorder] Invalid duration detected, falling back to null');
          duration = null;
        }

        const newRecording = {
          id,
          label,
          url: null,
          duration,
          createdAt,
          storagePath: null,
          localBlob: blob
        };

        let updatedRecordings = [];

        if (tune?.id) {
          updatedRecordings = [...(tune.recordings || []), newRecording];
          await dexie.tunes.update(tune.id, { recordings: updatedRecordings });
          await syncTuneRecordings({ ...tune, recordings: updatedRecordings }, user);
        } else {
          updatedRecordings = [...(recordings || []), newRecording];
        }

        if (onUpdateRecordings) {
          onUpdateRecordings(updatedRecordings);
        }

        if (onRecordingSaved) {
          onRecordingSaved(newRecording);
        }

        setLabel('');
      };

      // Sometimes on iOS, metadata doesn't fire — fallback handler
      setTimeout(() => {
        if (!audioRef.current?.duration || !isFinite(audioRef.current.duration)) {
          console.warn('[TuneRecorder] Metadata load timeout — using fallback path');
          audio.onloadedmetadata();
        }
      }, 2000);

    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
  };

  const formatElapsedTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div style={{ marginTop: '1rem', textAlign: 'center', position: 'relative' }}>
      <input
        type="text"
        placeholder="Recording label"
        value={label}
        onChange={e => setLabel(e.target.value)}
        disabled={isRecording}
        style={{
          padding: '6px',
          marginRight: '8px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          width: '200px'
        }}
      />
      {isRecording && (
        <div style={{ marginBottom: '8px', fontSize: '0.85rem', color: '#666' }}>
          Recording time: {formatElapsedTime(elapsedTime)}
        </div>
      )}
      {!isRecording ? (
        <button onClick={handleStartRecording} className="big-button">🎙 Start Recording</button>
      ) : (
        <button
          onClick={handleStopRecording}
          className="big-button"
          style={{ backgroundColor: '#e00', color: '#fff' }}
        >
          ⏹ Stop
        </button>
      )}
      {isRecording && (
        <div style={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 12,
          height: 12,
          backgroundColor: 'red',
          borderRadius: '50%',
          animation: 'pulse-dot 1s infinite'
        }} />
      )}
      <style>{`
        @keyframes pulse-dot {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TuneRecorder;
